import { useState } from 'react'
import { Card } from './Card'
import { BarList, DayBars, Funnel, GroupTable, HourBars, dayLabel, fmt, trendSeries } from './UsageCharts'
import { appShare, featureTotals, heldText, pctOf, type FeatureKey, type Platform, type UsageData } from '../lib/usage'

/**
 * 301. The usage page's body, from one usage_dashboard() result. Separate from
 * routes/AdminUsage.tsx so the suite can render it (the route imports the
 * shell). Receives counts only; see migration 301.
 */

export const FEATURE_LABEL: Record<FeatureKey, string> = {
  posts: 'Posts and notices published',
  recognitions: 'Recognitions given',
  replies: 'Replies',
  one_question: 'One Question answers',
  ask_one: 'Questions to Ask One',
  video_started: 'Welcome video started',
  contest_entries: 'Photo contest entries',
  support_questions: 'Private questions to support',
}

export const PLATFORM_LABEL: Record<Platform, string> = {
  ios: 'iPhone and iPad',
  android: 'Android',
  windows: 'Windows',
  macos: 'Mac',
  other: 'Other',
}

const FORM_LABEL = { phone: 'phone', tablet: 'tablet', computer: 'computer' } as const

function countryName(code: string): string {
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(code) ?? code
  } catch {
    return code
  }
}

export function UsageBody({ data: d }: { data: UsageData }) {
  const [by, setBy] = useState<'country' | 'brand'>('brand')
  const k = d.k_people
  const kv = d.k_visits
  const kp = d.kpis
  const t = trendSeries(d.trend)
  const share = appShare(d.trend)
  const firstSnapshot = d.trend.find((x) => x.active !== null)?.day

  const tiles: Array<{ k: string; v: string; sub: string }> = [
    { k: 'Active today', v: fmt(kp.active_24h), sub: `people in the last 24 hours · ${pctOf(kp.active_24h, kp.signed_in_ever) || '0%'} of those signed in` },
    { k: 'Active this week', v: fmt(kp.active_7d), sub: `people in the last 7 days · ${pctOf(kp.active_7d, kp.signed_in_ever) || '0%'}` },
    { k: 'Active this month', v: fmt(kp.active_30d), sub: 'people in the last 30 days' },
    { k: 'Signed in at least once', v: fmt(kp.signed_in_ever), sub: `of ${fmt(kp.staff)} staff · ${pctOf(kp.signed_in_ever, kp.staff) || '0%'}` },
    { k: 'Sign-ins today', v: fmt(kp.sign_ins_24h), sub: `last 24 hours · ${fmt(kp.first_sign_ins_7d)} new people this week` },
    { k: 'Devices today', v: fmt(kp.visits_today), sub: share === null ? 'opened the hub today (UTC)' : `${share}% as the installed app, last ${d.days} days` },
  ]

  const network = [
    ...d.network.countries.map((c) => ({ key: c.country, label: countryName(c.country), n: c.visits as number | null })),
    ...(d.network.other !== 0 ? [{ key: 'other', label: 'Other countries, together', n: d.network.other }] : []),
    ...(d.network.unknown !== 0 ? [{ key: 'ZZ', label: 'Not known', n: d.network.unknown }] : []),
  ]

  const people = d.devices.people.map((p) => ({
    key: `${p.os}-${p.form}`,
    label: `${PLATFORM_LABEL[p.os] ?? p.os} ${FORM_LABEL[p.form] ?? p.form}`,
    n: p.people,
  }))
  const modes = d.devices.modes.map((m) => ({
    key: `${m.platform}-${m.display_mode}`,
    label: `${PLATFORM_LABEL[m.platform] ?? m.platform} · ${m.display_mode === 'app' ? 'installed app' : 'browser tab'}`,
    n: m.visits,
  }))

  const features = featureTotals(d).map((f) => ({
    key: f.key,
    label: FEATURE_LABEL[f.key],
    n: f.n as number | null,
    sub: f.key === 'video_started' ? 'devices' : f.people === undefined ? undefined : `by ${heldText(f.people, k, fmt)} people`,
  }))

  return (
    <div className="usage">
      <div className="askone__tiles">
        {tiles.map((x) => (
          <div className="askone__tile" key={x.k}>
            <span className="askone__tilek">{x.k}</span>
            <span className="askone__tilev">{x.v}</span>
            <span className="askone__tilesub">{x.sub}</span>
          </div>
        ))}
      </div>

      <Card title="People active each day">
        <DayBars
          days={t.days}
          series={t.active}
          label="People who used the hub each day"
          missing={
            firstSnapshot
              ? `A day's figure is counted at 23:55 UTC; days before ${dayLabel(firstSnapshot)} have none.`
              : 'A day\'s figure is counted at 23:55 UTC from the day this page went live; today is counted as it stands.'
          }
        />
      </Card>

      <div className="usage__stack">
        <Card title="Sign-ins each day">
          <DayBars days={t.days} series={t.signIns} label="Sign-ins each day, first sign-ins and returning" />
          <p className="askone__note">A sign-in is entering a code. Staying signed in is not a new one, so this falls as people settle in.</p>
        </Card>
        <Card title="Devices that opened the hub, each day">
          <DayBars days={t.days} series={t.devices} label="Devices that opened the hub each day, installed app or browser tab" />
          <p className="askone__note">Each device counts once a day. A phone and a laptop are two; a shared tablet is one.</p>
        </Card>
      </div>

      <Card title="From access to use">
        <Funnel
          steps={[
            { label: 'Staff', n: kp.staff, hint: 'active in HR' },
            { label: 'Can sign in', n: kp.can_sign_in, hint: 'have an email or phone to sign in with' },
            { label: 'Signed in', n: kp.signed_in_ever, hint: 'at least once' },
            { label: 'Active this week', n: kp.active_7d, hint: 'used the hub in the last 7 days' },
            { label: 'Added a photo', n: kp.with_photo, hint: 'on their card' },
          ]}
        />
        <div className="chips usage__by" role="group" aria-label="Group by">
          {(['brand', 'country'] as const).map((b) => (
            <button key={b} type="button" className={`chip chip--sm${by === b ? ' chip--on' : ''}`} aria-pressed={by === b} onClick={() => setBy(b)}>
              {b === 'brand' ? 'By company' : 'By country of work'}
            </button>
          ))}
        </div>
        <GroupTable rows={by === 'brand' ? d.by_brand : d.by_country} k={k} first={by === 'brand' ? 'Company' : 'Country of work'} />
        <p className="askone__note">
          Where people work comes from the HR record. A number of people under {k} shows as “fewer than {k}”, and groups of
          fewer than {k} staff are added together, so no figure points at one person.
        </p>
      </Card>

      <div className="askone__two">
        <Card title="Where people connect from">
          <BarList rows={network} k={kv} label="Devices by the country of their connection" empty="Nothing counted yet. Each device counts once a day from the day this page went live." />
          <p className="askone__note">
            The country of the internet connection, devices a day over the last {d.days} days ({fmt(d.network.total)} in all).
            Countries under {kv} are added together. Nothing about the connection is kept but the country.
          </p>
        </Card>
        <Card title="Phones and computers">
          <BarList rows={people} k={k} label="People by the device they used" empty="No sessions in this period." />
          <p className="askone__note">
            People who used each kind of device in the last {d.days} days ({fmt(d.devices.people_total)} people). Somebody on a phone and a laptop counts in both.
          </p>
          <h3 className="panel__h3">Installed app or browser tab</h3>
          <BarList rows={modes} k={kv} label="Devices by installed app or browser tab" empty="Nothing counted yet." />
          <p className="askone__note">Devices a day. A count under {kv} shows as “fewer than {kv}”.</p>
        </Card>
      </div>

      <Card title="What people use">
        <BarList rows={features} k={k} label="Things done on the hub in the period" empty="Nothing yet." />
        <p className="askone__note">Last {d.days} days. “By” counts different people; under {k} shows as “fewer than {k}”.</p>
      </Card>

      <div className="usage__stack">
        <Card title="When the hub is opened">
          <HourBars rows={d.hours.opens} label="Hub opened, by hour of the day on the device" />
          <p className="askone__note">Last four weeks, by the hour on the person's own device. Each device counts once an hour.</p>
        </Card>
        <Card title="When people sign in">
          <HourBars rows={d.hours.sign_ins} label="Sign-ins by local hour at the person's site" />
          <p className="askone__note">Last 28 days, by the hour at the person's work site.</p>
        </Card>
      </div>

      <p className="askone__note usage__foot">
        Updated {new Date(d.generated_at).toLocaleString('en-CA', { dateStyle: 'medium', timeStyle: 'short' })} · Days are UTC ·
        Nothing on this page can show who did what.
      </p>
    </div>
  )
}
